import React, { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  Container,
  Button,
  MenuItem,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { styled } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import logo from '../../assets/images/infhire_logo.webp';
import logoSm from '../../assets/images/infhire_logo_sm.webp';
import { useAuth } from "../../contexts/AuthContext";
import { useColorMode } from '../../contexts/ThemeContext';
import RoleSwitcher from '../auth/RoleSwitcher';

// Enhanced styled components with glassmorphism and mobile optimizations
const GlassAppBar = styled(AppBar)(({ theme }) => {
  const dark = theme.palette.mode === 'dark';
  return {
    background: dark ? 'rgba(18, 18, 32, 0.92)' : 'rgba(255, 255, 255, 0.85)',
    backdropFilter: 'blur(20px)',
    border: dark ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(255, 255, 255, 0.3)',
    boxShadow: dark ? '0 8px 32px rgba(0, 0, 0, 0.4)' : '0 8px 32px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(255, 255, 255, 0.2)',
    borderBottom: dark ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(255, 255, 255, 0.2)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    position: 'sticky',
    top: 0,
    zIndex: theme.zIndex.appBar,

    [theme.breakpoints.down('md')]: {
      backdropFilter: 'blur(12px)',
      background: dark ? 'rgba(18, 18, 32, 0.97)' : 'rgba(255, 255, 255, 0.95)',
      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
      '&:hover': {
        backdropFilter: 'blur(12px)',
        background: dark ? 'rgba(18, 18, 32, 0.97)' : 'rgba(255, 255, 255, 0.95)',
      },
    },

    '&:hover': {
      background: dark ? 'rgba(18, 18, 32, 0.97)' : 'rgba(255, 255, 255, 0.95)',
      backdropFilter: 'blur(25px)',
      boxShadow: dark ? '0 12px 40px rgba(0, 0, 0, 0.5)' : '0 12px 40px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(255, 255, 255, 0.3)',
      '@media (hover: none)': {
        background: dark ? 'rgba(18, 18, 32, 0.92)' : 'rgba(255, 255, 255, 0.85)',
        backdropFilter: 'blur(12px)',
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
      },
    },
  };
});

const GlassMenu = styled(Menu)(({ theme }) => {
  const dark = theme.palette.mode === 'dark';
  const menuBg = dark ? 'rgba(28, 28, 45, 0.97)' : 'rgba(255, 255, 255, 0.9)';
  return {
  '& .MuiPaper-root': {
    background: menuBg,
    backdropFilter: 'blur(20px)',
    border: dark ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(255, 255, 255, 0.3)',
    borderRadius: '16px',
    boxShadow: dark ? '0 12px 40px rgba(0,0,0,0.5)' : '0 12px 40px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(255, 255, 255, 0.2)',
    marginTop: theme.spacing(1),
    overflow: 'visible',

    [theme.breakpoints.down('md')]: {
      backdropFilter: 'blur(12px)',
      borderRadius: '12px',
      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
      marginTop: theme.spacing(0.5),
      '&::before': { display: 'none' },
    },

    '&::before': {
      content: '""',
      position: 'absolute',
      top: '-8px',
      left: '20px',
      width: '16px',
      height: '16px',
      background: menuBg,
      border: dark ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(255, 255, 255, 0.3)',
      borderBottom: 'none',
      borderRight: 'none',
      transform: 'rotate(45deg)',
      backdropFilter: 'blur(20px)',
    },
  },
  
  '& .MuiMenuItem-root': {
    borderRadius: '12px',
    margin: '4px 8px',
    minHeight: '44px', // Minimum touch target
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    
    [theme.breakpoints.down('md')]: {
      minHeight: '48px', // Larger touch targets on mobile
      margin: '2px 4px',
      fontSize: '1rem',
    },
    
    '&:hover': {
      background: 'rgba(25, 118, 210, 0.1)',
      transform: 'translateX(4px)',
    },
    
    // Replace hover with touch feedback on mobile
    '@media (hover: none)': {
      '&:hover': {
        background: 'rgba(25, 118, 210, 0.1)',
        transform: 'none',
      },
      
      '&:active': {
        background: 'rgba(25, 118, 210, 0.15)',
        transform: 'scale(0.98)',
      }
    }
  }
  };
});

const EnhancedButton = styled(Button)(({ theme }) => ({
  borderRadius: '12px',
  fontWeight: 500,
  padding: '8px 16px',
  minHeight: '44px', // Minimum touch target
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  overflow: 'hidden',
  
  // Mobile optimizations
  [theme.breakpoints.down('md')]: {
    minHeight: '48px', // Larger touch targets on mobile
    padding: '10px 20px',
    fontSize: '1rem',
    
    // Disable shimmer on mobile for performance
    '&::before': {
      display: 'none',
    }
  },
  
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '100%',
    background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)',
    transition: 'left 0.5s ease',
  },
  
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 20px rgba(25, 118, 210, 0.2)',
    
    '&::before': {
      left: '100%',
    }
  },
  
  // Replace hover with touch feedback on mobile
  '@media (hover: none)': {
    '&:hover': {
      transform: 'none',
      boxShadow: 'none',
    },
    
    '&:active': {
      transform: 'scale(0.95)',
      background: 'rgba(25, 118, 210, 0.15)',
    }
  },
  
  '&.nav-button': {
    color: theme.palette.text.primary,
    
    '&:hover': {
      background: 'rgba(25, 118, 210, 0.08)',
      color: theme.palette.primary.main,
    },
    
    '@media (hover: none)': {
      '&:hover': {
        background: 'transparent',
        color: theme.palette.text.primary,
      },
      
      '&:active': {
        background: 'rgba(25, 118, 210, 0.12)',
        color: theme.palette.primary.main,
      }
    }
  }
}));

const AuthContainer = styled(Box)(({ theme }) => {
  const dark = theme.palette.mode === 'dark';
  return {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(0.5),
    borderRadius: '16px',
    background: dark ? 'rgba(28, 28, 45, 0.8)' : 'rgba(255, 255, 255, 0.7)',
    backdropFilter: 'blur(12px)',
    border: dark ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(255, 255, 255, 0.3)',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',

    [theme.breakpoints.down('md')]: {
      gap: theme.spacing(0.5),
      padding: theme.spacing(0.25),
      borderRadius: '12px',
      backdropFilter: 'blur(8px)',
      background: dark ? 'rgba(28, 28, 45, 0.95)' : 'rgba(255, 255, 255, 0.85)',
      '&:hover': {
        transform: 'none',
        background: dark ? 'rgba(28, 28, 45, 0.95)' : 'rgba(255, 255, 255, 0.85)',
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
      },
    },

    '&:hover': {
      background: dark ? 'rgba(28, 28, 45, 0.95)' : 'rgba(255, 255, 255, 0.85)',
      boxShadow: '0 6px 20px rgba(0, 0, 0, 0.15)',
      transform: 'translateY(-1px)',
    },

    '@media (hover: none)': {
      '&:hover': {
        background: dark ? 'rgba(28, 28, 45, 0.8)' : 'rgba(255, 255, 255, 0.7)',
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
        transform: 'none',
      },
    },
  };
});

const LogoContainer = styled(Box)(({ theme }) => ({
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  borderRadius: '12px',
  padding: '4px',
  
  // Mobile optimizations
  [theme.breakpoints.down('md')]: {
    padding: '2px',
    
    '&:hover': {
      transform: 'none', // Disable scale on mobile
      background: 'transparent',
    }
  },
  
  '&:hover': {
    transform: 'scale(1.05)',
    background: 'rgba(255, 255, 255, 0.1)',
  },
  
  // Touch feedback for mobile
  '@media (hover: none)': {
    '&:hover': {
      transform: 'none',
      background: 'transparent',
    },
    
    '&:active': {
      transform: 'scale(0.98)',
    }
  }
}));

const pages = [
  { title: 'Home', path: '/' },
  { title: 'How It Works', path: '/how-it-works' },
  { title: 'About', path: '/about' },
  { title: 'Help', path: '/help' }
];

const Navbar = () => {
  const [anchorElNav, setAnchorElNav] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user, signOut, userRoles, activeRole } = useAuth();
  const { mode, toggleColorMode } = useColorMode();
  const navigate = useNavigate();

  const handleOpenNavMenu = (event) => {
    setAnchorElNav(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };
  
  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  return (
    <GlassAppBar position="static" color="transparent" elevation={0}>
      <Container maxWidth="xl">
        <Toolbar disableGutters>
          {/* Logo for larger screens */}
          <LogoContainer sx={{ display: { xs: 'none', md: 'flex' }, mr: 2, alignItems: 'center' }}>
            <RouterLink to="/">
              <img src={logo} srcSet={`${logoSm} 160w, ${logo} 300w`} sizes="(max-width: 900px) 64px, 82px" alt="InfHire Logo" width={82} height={56} style={{ height: '56px' }} />
            </RouterLink>
          </LogoContainer>

          {/* Mobile menu */}
          <Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' } }}>
            <IconButton
              size="large"
              aria-label="menu"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleOpenNavMenu}
              color="inherit"
              sx={{
                borderRadius: '12px',
                minWidth: '48px',
                minHeight: '48px',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                
                '&:hover': {
                  background: 'rgba(25, 118, 210, 0.08)',
                  transform: 'scale(1.1)',
                },
                
                // Touch optimizations
                '@media (hover: none)': {
                  '&:hover': {
                    background: 'rgba(25, 118, 210, 0.08)',
                    transform: 'none',
                  },
                  
                  '&:active': {
                    background: 'rgba(25, 118, 210, 0.15)',
                    transform: 'scale(0.95)',
                  }
                }
              }}
            >
              <MenuIcon />
            </IconButton>
            <GlassMenu
              id="menu-appbar"
              anchorEl={anchorElNav}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              open={Boolean(anchorElNav)}
              onClose={handleCloseNavMenu}
              sx={{
                display: { xs: 'block', md: 'none' },
              }}
            >
              {pages.map((page) => (
                <MenuItem key={page.title} onClick={handleCloseNavMenu} component={RouterLink} to={page.path}>
                  <Typography textAlign="center">{page.title}</Typography>
                </MenuItem>
              ))}
            </GlassMenu>
          </Box>

          {/* Logo for mobile */}
          <LogoContainer sx={{ display: { xs: 'flex', md: 'none' }, flexGrow: 1, justifyContent: 'center' }}>
            <RouterLink to="/">
              <img src={logo} srcSet={`${logoSm} 160w, ${logo} 300w`} sizes="64px" alt="InfHire Logo" width={64} height={44} style={{ height: '44px' }} />
            </RouterLink>
          </LogoContainer>

          {/* Desktop menu */}
          <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' }, justifyContent: 'center' }}>
            {pages.map((page) => (
              <EnhancedButton
                key={page.title}
                component={RouterLink}
                to={page.path}
                onClick={handleCloseNavMenu}
                className="nav-button"
                sx={{ my: 2, display: 'block', mx: 1 }}
              >
                {page.title}
              </EnhancedButton>
            ))}
          </Box>

          {/* Auth buttons */}
          <AuthContainer sx={{ flexGrow: 0 }}>
            {/* Theme toggle */}
            <IconButton
              onClick={toggleColorMode}
              size="small"
              aria-label={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              sx={{ color: 'text.secondary' }}
            >
              {mode === 'dark' ? <Brightness7Icon fontSize="small" /> : <Brightness4Icon fontSize="small" />}
            </IconButton>

            {user ? (
              <>
                {/* Role Switcher */}
                <RoleSwitcher />
                
                {/* Add Role button if user doesn't have both roles */}
                {userRoles && userRoles.length < 2 && (
                  <EnhancedButton 
                    component={RouterLink} 
                    to="/add-role"
                    color="inherit"
                    size="small"
                    className="nav-button"
                  >
                    Add Role
                  </EnhancedButton>
                )}
                
                {/* Dashboard button */}
                {activeRole && (
                  <EnhancedButton 
                    component={RouterLink} 
                    to={`/${activeRole}`}
                    variant="outlined"
                    color="primary"
                    size="small"
                  >
                    Dashboard
                  </EnhancedButton>
                )}
                
                {/* Logout button */}
                <EnhancedButton 
                  color="inherit" 
                  onClick={handleSignOut}
                  size="small"
                  className="nav-button"
                >
                  Logout
                </EnhancedButton>
              </>
            ) : (
              isMobile ? (
                <EnhancedButton 
                  component={RouterLink} 
                  to="/login" 
                  variant="contained" 
                  color="primary"
                  size="small"
                >
                  Login
                </EnhancedButton>
              ) : (
                <>
                  <EnhancedButton 
                    component={RouterLink} 
                    to="/login" 
                    variant="outlined" 
                    color="primary"
                    size="small"
                    sx={{ mr: 1 }}
                  >
                    Login
                  </EnhancedButton>
                  <EnhancedButton 
                    component={RouterLink} 
                    to="/register" 
                    variant="contained" 
                    color="primary"
                    size="small"
                  >
                    Register
                  </EnhancedButton>
                </>
              )
            )}
          </AuthContainer>
        </Toolbar>
      </Container>
    </GlassAppBar>
  );
};

export default Navbar;
